import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import classes from './LinkButton.module.css';

export type LinkButtonProps = {
  to: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'large' | 'medium' | 'small';
  className?: string;
  ariaLabel?: string;
};

/**
 * A Link component styled as a Button.
 *
 * This component provides a semantic <a> tag (via React Router Link)
 * with button styling, avoiding the accessibility issue of nesting
 * interactive elements (button inside link).
 *
 * @example
 * <LinkButton to="/teams/create" variant="primary">
 *   Create Team
 * </LinkButton>
 */
export const LinkButton = ({
  to,
  children,
  variant = 'primary',
  size = 'large',
  className,
  ariaLabel,
}: LinkButtonProps) => {
  const combinedClassName = cn(
    classes.linkButton,
    classes[variant],
    classes[size],
    className
  );

  return (
    <Link
      to={to}
      className={combinedClassName}
      role="button"
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
};
